import { supabase } from '@/integrations/supabase/client';
import type { Lead, LeadInsert, LeadUpdate, QuickLeadInsert } from '@/types/lead';

type NotifyEvent = 'created' | 'completed' | 'callback_requested' | 'quick_contact';

/**
 * Best-effort notification to the team (ads@yawctv.com) via the `notify-lead`
 * edge function. This is intentionally fire-and-forget: a notification failure
 * (e.g. Resend not configured yet) must never block the visitor's progress or
 * surface an error in the UI. The edge function re-reads the row server-side
 * using the id + session_token, so no lead data is trusted from the client.
 */
function notifyLead(id: string, sessionToken: string, event: NotifyEvent): void {
  try {
    void supabase.functions
      .invoke('notify-lead', { body: { id, session_token: sessionToken, event } })
      .catch(() => {
        /* swallow — notifications are best-effort */
      });
  } catch {
    /* swallow — notifications are best-effort */
  }
}

/**
 * Create a lead on step 1 of the guided form.
 *
 * The id and session_token are generated on the client so we never need SELECT
 * access on the leads table to read them back — the table is intentionally
 * locked down for the anon role (see the security-hardening migration). The row
 * is persisted immediately, so a visitor who abandons the form after step 1 is
 * still captured and routed to the team.
 */
export async function createLead(contactInfo: LeadInsert): Promise<{ id: string; session_token: string }> {
  const id = crypto.randomUUID();
  const session_token = crypto.randomUUID();

  const { error } = await supabase
    .from('leads')
    .insert([{ id, session_token, ...contactInfo, status: 'started', current_step: 1 }]);

  if (error) throw error;

  // Notify right away so partial / abandoned leads still reach the team.
  notifyLead(id, session_token, 'created');

  return { id, session_token };
}

/**
 * One-shot "quick contact" submission from the homepage form. This form does
 * not collect a phone number, so phone is left null (allowed by the schema).
 */
export async function createQuickLead(input: QuickLeadInsert): Promise<{ id: string; session_token: string }> {
  const id = crypto.randomUUID();
  const session_token = crypto.randomUUID();

  const { error } = await supabase.from('leads').insert([
    {
      id,
      session_token,
      full_name: input.full_name,
      email: input.email,
      phone: null,
      organization_name: input.organization_name ?? null,
      additional_notes: input.message,
      status: 'quick_contact',
      current_step: 1,
    },
  ]);

  if (error) throw error;

  notifyLead(id, session_token, 'quick_contact');

  return { id, session_token };
}

export async function updateLead(id: string, sessionToken: string, updates: LeadUpdate): Promise<Lead> {
  const { data, error } = await supabase.rpc('update_lead_with_token', {
    p_id: id,
    p_session_token: sessionToken,
    p_updates: JSON.parse(JSON.stringify(updates)),
  });

  if (error) throw error;
  return data as unknown as Lead;
}

export async function requestCallback(id: string, sessionToken: string): Promise<Lead> {
  const lead = await updateLead(id, sessionToken, { status: 'callback_requested' });
  notifyLead(id, sessionToken, 'callback_requested');
  return lead;
}

export async function completeLead(id: string, sessionToken: string, finalData: LeadUpdate): Promise<Lead> {
  const lead = await updateLead(id, sessionToken, {
    ...finalData,
    status: 'complete',
    completed_at: new Date().toISOString(),
    current_step: 4,
  });
  notifyLead(id, sessionToken, 'completed');
  return lead;
}
